<template>
  <div>
    <div class="flex items-center justify-between pt-3">
      <div>
        <NewItemDropdown
          :items="newItemOptions"
          @item-selected="launchNewItemModal"
        />
        </div>
      <div>
        <d-btn color="secondary" size="sm" :loading="saving" @click="saveDraggable">
          Save Changes
        </d-btn>
      </div>
    </div>
    <div class="flex divide-x-4 divide-secondary-400 -mx-10 py-10">
      <div class="w-1/2 px-10">
        <categorized-files-tree v-model="categorizedFiles" :data="categorizedFiles" group="files" />
      </div>
      <div class="w-1/2 px-10">
        <p class="text-3xl font-bold mb-5">
          Uncategorized Files
        </p>
        <draggable
          v-if="uncategorizedFiles.length > 0"
          :list="uncategorizedFiles"
          :group="{name: 'files', put: false, pull: true}"
          class="divide-y divide-secondary-500"
          ghost-class="ghost"
          @start="drag = true"
          @end="drag = false"
          item-key="id"
        >
          <template #item="{element}">
            <list-item :file="element" class="cursor-pointer" />
          </template>
        </draggable>
        <div v-else>
          No uncategorized files are available at this time.
        </div>
      </div>
    </div>

    <!-- Modals -->
    <d-modal
      :active="showLinkModal"
      :width="850"
      @close="closeLinkModal"
      :can-cancel="['escape', 'x', 'outside']"
    >
      <LinkModal
        v-bind="currentModalProps"
        @close="closeLinkModal"
        @saved="handleModalSaved"
      />
    </d-modal>

    <d-modal
      :active="showHtmlModal"
      :width="850"
      @close="closeHtmlModal"
      :can-cancel="['escape', 'x', 'outside']"
    >
      <HtmlModal
        v-bind="currentModalProps"
        @close="closeHtmlModal"
        @saved="handleModalSaved"
      />
    </d-modal>

    <d-modal
      :active="showQuizModal"
      :width="850"
      @close="closeQuizModal"
      :can-cancel="['escape', 'x', 'outside']"
    >
      <QuizModal
        v-bind="currentModalProps"
        @close="closeQuizModal"
        @saved="handleModalSaved"
      />
    </d-modal>

    <d-modal
      :active="showExamModal"
      :width="850"
      @close="closeExamModal"
      :can-cancel="['escape', 'x', 'outside']"
    >
      <ExamModal
        v-bind="currentModalProps"
        @close="closeExamModal"
        @saved="handleModalSaved"
      />
    </d-modal>
  </div>
</template>

<script>
  import { getItems, bulkUpdateItems } from '@resources/scripts/services/contentListingItemsApi.service';
  import { getExams, bulkUpdateExams } from '@resources/scripts/services/examsApi.service';
  import { ContentListingType, ListingItemType } from '@resources/scripts/constants';
  import Draggable from 'vuedraggable';
  import ListItem from './ListItem/ListItem.vue';
  import CategorizedFilesTree from './Organization/CategorizedFilesTree.vue';
  import NewItemDropdown from './Organization/NewItemDropdown.vue';
  import LinkModal from './Organization/LinkModal.vue';
  import HtmlModal from './Organization/HtmlModal.vue';
  import QuizModal from './Organization/QuizModal.vue';
  import ExamModal from './Organization/ExamModal.vue';

  export default {
    inject: ['contentListing'],
    provide() {
      return {
        getFiles: async () => this.getFiles(),
        saveDraggable: async () => this.saveDraggable(),
      };
    },

    components: {
      CategorizedFilesTree,
      Draggable,
      ListItem,
      NewItemDropdown,
      LinkModal,
      HtmlModal,
      QuizModal,
      ExamModal,
    },

    data() {
      return {
        ListingItemType,
        saving: false,
        loading: true,
        uncategorizedFiles: [],
        categorizedFiles: [],
        showLinkModal: false,
        showHtmlModal: false,
        showQuizModal: false,
        showExamModal: false,
        currentModalProps: {},
        newItemOptions: [
          { label: 'New Link', value: ListingItemType.LINK },
          { label: 'New HTML/Embedded', value: ListingItemType.EMBEDDED },
          { label: 'New Quiz', value: ListingItemType.QUIZ },
          { label: 'New Exam', value: ListingItemType.EXAM }
        ]
      };
    },

    mounted() {
      this.getFiles();
    },

    methods: {
      async getFiles() {
        this.loading = true;
        
        // Get both content listing items and exams
        const [contentItems, exams] = await Promise.all([
          getItems(this.contentListing.id, this.contentListing.type, false),
          getExams(this.contentListing.id)
        ]);
        
        // Transform exams to look like content items for display
        // Exams should always be in the categorized area
        const transformedExams = exams.map(exam => ({
          ...exam,
          type: 'Exam',
          content: 'Exam',
          isExam: true, // Flag to identify this as an exam
          categorized: true // Always categorized
        }));
        
        // Get categorized content items and combine with exams
        const categorizedContentItems = await getItems(this.contentListing.id, this.contentListing.type, true);
        this.categorizedFiles = [...categorizedContentItems, ...transformedExams].sort((a, b) => a.sortIndex - b.sortIndex);
        
        // Only show uncategorized content items (not exams)
        this.uncategorizedFiles = contentItems.filter(item => !item.categorized).sort((a, b) => a.sortIndex - b.sortIndex);
        
        this.loading = false;
      },

      async saveDraggable() {
        this.saving = true;
        let rearrangedFiles = JSON.parse(JSON.stringify(this.categorizedFiles));
        
        // Separate content items and exams
        const contentItems = [];
        const exams = [];
        
        rearrangedFiles.forEach((item, index) => {
          item.categorized = true;
          item.sortIndex = index;
          
          if (item.isExam) {
            // This is an exam
            exams.push({
              id: item.id,
              item: {
                id: item.id,
                name: item.name,
                description: item.description,
                allowMultipleAttempts: item.allowMultipleAttempts,
                passPercentage: item.passPercentage,
                password: item.password,
                sortIndex: index
              }
            });
          } else {
            // This is a content listing item
            contentItems.push({
              id: item.id,
              item
            });
          }
        });
        
        // Update content items and exams in parallel
        const promises = [];
        
        if (contentItems.length > 0) {
          promises.push(bulkUpdateItems(contentItems, ContentListingType.INTERACTIVE));
        }
        
        if (exams.length > 0) {
          promises.push(bulkUpdateExams(exams));
        }
        
        await Promise.all(promises);
        
        this.saving = false;
        return rearrangedFiles;
      },

      launchNewItemModal(itemType) {
        this.currentModalProps = {
          contentListing: this.contentListing,
        };

        switch (itemType) {
          case ListingItemType.EMBEDDED:
            this.showHtmlModal = true;
            break;
          case ListingItemType.LINK:
            this.showLinkModal = true;
            break;
          case ListingItemType.QUIZ:
            this.showQuizModal = true;
            break;
          case ListingItemType.EXAM:
            this.showExamModal = true;
            break;
          default:
            return;
        }
      },

      closeLinkModal() {
        this.showLinkModal = false;
        this.currentModalProps = {};
      },

      closeHtmlModal() {
        this.showHtmlModal = false;
        this.currentModalProps = {};
      },

      closeQuizModal() {
        this.showQuizModal = false;
        this.currentModalProps = {};
      },

      closeExamModal() {
        this.showExamModal = false;
        this.currentModalProps = {};
      },

      async handleModalSaved() {
        await this.getFiles();
        await this.saveDraggable();
      },
    },
  };
</script>